<template>
  <v-dialog v-model="localModelValue" max-width="736">
    <v-card class="change-email-modal">
      <div class="modal-content-wrapper">
        <div class="modal-step">
          <div class="modal-header-section">
            <h2 class="modal-main-title">Primary Email</h2>
            <p class="modal-subtitle">Choose email from sign in methods</p>
          </div>

          <div class="email-radio-group">
            <label
              v-for="emailOption in availableEmails"
              :key="emailOption.email"
              class="email-radio-option"
            >
              <input
                type="radio"
                :value="emailOption.email"
                v-model="selectedEmail"
                class="email-radio-input"
              />
              <span class="email-radio-custom"></span>
              <span class="email-radio-label">{{ emailOption.email }}</span>
            </label>
          </div>

          <div class="modal-actions-container">
            <ActionButton
              variant="primary"
              class="modal-btn modal-btn-save"
              :class="{ 'modal-btn-disabled': !isEmailSelected }"
              :disabled="!isEmailSelected || isLoading"
              :loading="isLoading"
              @click="handleSave"
            >
              Save
            </ActionButton>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch, defineProps, defineEmits, inject } from "vue";
import ActionButton from "../common/ActionButton.vue";
import userService from "@/services/userService";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  user: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "continue"]);

const localModelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const selectedEmail = ref("");
const isLoading = ref(false);
const notify = inject("notify");

const availableEmails = computed(() => {
  const emails = [];

  // Get emails from accounts array
  if (props.user?.accounts?.length > 0) {
    props.user.accounts.forEach((account) => {
      if (account.email) {
        emails.push({
          email: account.email,
          type: account.source?.toLowerCase(),
          isPrimary: account.is_primary,
          accountId: account.id,
        });
      }
    });
  }

  return emails;
});

const primaryEmail = computed(() => {
  const primary = availableEmails.value.find((email) => email.isPrimary);
  return primary?.email || null;
});

const isEmailSelected = computed(() => {
  if (!selectedEmail.value.trim().length) {
    return false;
  }
  // Disable if selected email is the same as primary email
  return selectedEmail.value !== primaryEmail.value;
});

const reset = () => {
  selectedEmail.value = "";
  // Set default to primary email if it exists in the list, otherwise first email
  if (availableEmails.value.length > 0) {
    if (primaryEmail.value) {
      selectedEmail.value = primaryEmail.value;
    } else {
      selectedEmail.value = availableEmails.value[0].email;
    }
  } else if (props.user?.email) {
    selectedEmail.value = props.user.email;
  }
};

const handleSave = async () => {
  if (!isEmailSelected.value) {
    return;
  }

  // Find the account ID for the selected email
  const selectedAccount = availableEmails.value.find(
    (email) => email.email === selectedEmail.value
  );

  if (!selectedAccount?.accountId) {
    notify.error("Account ID not found for selected email");
    return;
  }

  isLoading.value = true;

  try {
    await userService.setPrimaryEmail(selectedAccount.accountId);
    notify.success("Profile has been changed");
    emit("continue", { email: selectedEmail.value });
    localModelValue.value = false;
  } catch (error) {
    console.error("Failed to set primary email:", error);
    notify.error(
      error?.response?.data?.message || "Failed to update primary email"
    );
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      reset();
    }
  }
);

watch(
  () => props.user,
  () => {
    if (props.modelValue) {
      reset();
    }
  },
  { deep: true }
);
</script>

<style scoped>
.change-email-modal {
  border-radius: 16px !important;
  overflow: visible;
  box-shadow: none;
}

.modal-content-wrapper {
  padding: 32px;
}

.modal-step {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.modal-header-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-main-title {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xxl);
  font-weight: 700;
  line-height: 36px;
  color: #3b4648;
  margin: 0;
}

.modal-subtitle {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
}

.email-radio-group {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
}

.email-radio-option {
  display: flex;
  align-items: center;
  padding: 0;
  cursor: pointer;
  gap: 0;
  width: 100%;
  min-height: 48px;
}

.email-radio-input {
  display: none;
}

.email-radio-custom {
  width: 24px;
  height: 24px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-right: 16px;
}

.email-radio-input:checked + .email-radio-custom {
  border-color: var(--primary-blue);
  background: white;
}

.email-radio-input:checked + .email-radio-custom::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: var(--primary-blue);
  border-radius: 50%;
}

.email-radio-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  flex: 1;
}

.modal-actions-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.modal-btn {
  height: 40px;
  min-width: 100px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.014px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.modal-btn-save {
  background-color: var(--primary-blue);
  color: #ffffff;
}

.modal-btn-save:hover:not(:disabled) {
  background-color: #002d4f;
}

.modal-btn-save:disabled {
  background-color: #dfe2e7;
  color: #cccccc;
}

.modal-btn-disabled {
  background-color: #dfe2e7 !important;
  color: #cccccc !important;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-content-wrapper {
    padding: 24px;
  }

  .modal-main-title {
    font-size: var(--fs-large);
    line-height: 28px;
  }
}
</style>
